import { FormGroup } from '@angular/forms';
import { FieldPermission } from './rpe-permissions.model';
import { ModeIcons } from '../utility/constants';
import {
  RpeFieldBinding,
  RpeFieldRegistry,
  RpeFieldRegistryEntry,
  RpeFormScope,
} from './rpe.model';

const PAGE_LEVEL_SCOPE: RpeFormScope = { tabId: -1, legendId: -1 };

export function isPageLevelFieldBinding(binding: RpeFieldBinding): boolean {
  return binding.tabId === -1 && binding.legendId === -1;
}

export function normalizeFieldIdentity(value: string): string {
  return value.trim().toLowerCase();
}

/** API may omit legendId for no-legend tabs (Notes); treat null/undefined/'' as -1. */
export function normalizeRpeScopeId(value: unknown): number {
  if (value == null || value === '') {
    return -1;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : -1;
}

export function getPermissionIdentityName(field: FieldPermission): string {
  const identity = field.identityName?.trim();
  if (identity) {
    return identity;
  }
  return field.fieldName;
}

export function fieldPermissionMatchesBinding(
  field: FieldPermission,
  binding: RpeFieldBinding,
  matchByIdentityNameOnly = false
): boolean {
  const fieldIdentity = normalizeFieldIdentity(
    getPermissionIdentityName(field)
  );
  const bindingIdentity = normalizeFieldIdentity(binding.identityName);

  if (matchByIdentityNameOnly) {
    // Allow "Agent Charge Code" (fieldName) to match agentChargeCode (control).
    return (
      fieldIdentity === bindingIdentity ||
      fieldIdentity.replace(/\s+/g, '') === bindingIdentity.replace(/\s+/g, '')
    );
  }

  if (fieldIdentity !== bindingIdentity) {
    return false;
  }

  if (isPageLevelFieldBinding(binding)) {
    return (
      normalizeRpeScopeId(field.tabId) === -1 &&
      normalizeRpeScopeId(field.legendId) === -1
    );
  }

  if (normalizeRpeScopeId(field.legendId) !== normalizeRpeScopeId(binding.legendId)) {
    return false;
  }

  if (
    binding.tabId !== -1 &&
    normalizeRpeScopeId(field.tabId) !== normalizeRpeScopeId(binding.tabId)
  ) {
    return false;
  }

  return true;
}

export function matchesFieldPermission(
  fieldPermissions: FieldPermission[],
  binding: RpeFieldBinding,
  matchByIdentityNameOnly = false
): boolean {
  return fieldPermissions.some((field) =>
    fieldPermissionMatchesBinding(field, binding, matchByIdentityNameOnly)
  );
}

export function registryEntryAppliesToFormScope(
  entry: RpeFieldRegistryEntry,
  formScope: RpeFormScope
): boolean {
  const binding = entry.binding;
  if (entry.matchByIdentityNameOnly) {
    // Identity-only fields apply to tab-scoped forms (not the page-level header form).
    return !(formScope.tabId === -1 && formScope.legendId === -1);
  }
  if (formScope.tabId === -1 && formScope.legendId === -1) {
    return isPageLevelFieldBinding(binding);
  }
  if (isPageLevelFieldBinding(binding)) {
    return false;
  }
  return (
    normalizeRpeScopeId(binding.legendId) ===
      normalizeRpeScopeId(formScope.legendId) &&
    (binding.tabId === -1 ||
      normalizeRpeScopeId(binding.tabId) === normalizeRpeScopeId(formScope.tabId))
  );
}

export function getRegistryBinding(
  entry: RpeFieldRegistryEntry,
  pageMode: string
): RpeFieldBinding | undefined {
  if (pageMode === ModeIcons.edit || pageMode === ModeIcons.bulkEdit) {
    return entry.binding;
  }
  return undefined;
}

export function isFieldEnforcementActive(
  enforcementEnabled: boolean,
  pageMode: string,
  _fieldPermissions: FieldPermission[] | undefined,
  hasAction: (actionName: string) => boolean
): boolean {
  if (!enforcementEnabled) {
    return false;
  }
  if (pageMode !== ModeIcons.edit && pageMode !== ModeIcons.bulkEdit) {
    return false;
  }
  // BR-006: Edit/Bulk Edit with empty fieldPermissions[] → all optional fields disabled.
  return hasAction('edit') || hasAction('bulk edit');
}

export function isFieldDisabledByPermissions(
  enforcementActive: boolean,
  fieldPermissions: FieldPermission[],
  binding: RpeFieldBinding | undefined,
  matchByIdentityNameOnly = false
): boolean {
  if (!enforcementActive || !binding) {
    return false;
  }
  return !matchesFieldPermission(
    fieldPermissions,
    binding,
    matchByIdentityNameOnly
  );
}

export function applyFieldPermissionsToForm(
  form: FormGroup,
  registry: RpeFieldRegistry,
  pageMode: string,
  isFieldDisabled: (entry: RpeFieldRegistryEntry) => boolean,
  formScope: RpeFormScope = PAGE_LEVEL_SCOPE
): void {
  for (const entry of registry) {
    if (!registryEntryAppliesToFormScope(entry, formScope)) {
      continue;
    }
    const binding = getRegistryBinding(entry, pageMode);
    const controlName = entry.binding.identityName;
    const control = form.get(controlName);
    if (!control || !binding) {
      continue;
    }
    if (isFieldDisabled(entry)) {
      control.disable({ emitEvent: false });
    }
  }
}
